/**
 * Gem Navigation Service
 * Handles navigation logic for gem items
 * Follows Single Responsibility Principle - only handles gem navigation
 */
import router from '@/router';
import { CitationParser } from '@/utils/citationParser';
export class GemNavigationService {
    static DEBUG_PREFIX = '[GemNavigationService]';
    /**
     * Handle gem item click navigation
     * Determines context and navigates appropriately
     *
     * @param item - The gem item clicked
     * @param context - Navigation context
     * @returns Promise<NavigationResult>
     */
    static async handleGemNavigation(item, context) {
        // Parse citation to get line number
        const citationResult = CitationParser.parseCitation(item.citation_id);
        if (!citationResult.isValid) {
            console.warn(`${this.DEBUG_PREFIX} Invalid citation:`, citationResult.error);
            return {
                success: false,
                message: citationResult.error || 'Invalid citation format',
                searchSuggestion: CitationParser.generateSearchSuggestion(item)
            };
        }
        const lineNumber = citationResult.lineNumber;
        // Determine navigation action based on context
        if (context.isProjectLevel) {
            return this.navigateToSessionLine(item, lineNumber, context);
        }
        else {
            return this.scrollToLineInSession(lineNumber, context);
        }
    }
    /**
     * Navigate to a different session and jump to line
     * Used from project-level gems
     *
     * @param item - Gem item with source session info
     * @param lineNumber - Line to jump to
     * @param context - Navigation context
     */
    static async navigateToSessionLine(item, lineNumber, context) {
        const sourceSessionId = item._source_session || context.sourceSessionId;
        if (!sourceSessionId) {
            console.error(`${this.DEBUG_PREFIX} No source session ID found for navigation`);
            return {
                success: false,
                message: 'Cannot determine source session'
            };
        }
        try {
            // Navigate to session with line number in hash
            await router.push({
                path: `/session/${sourceSessionId}`,
                hash: `#line-${lineNumber}`
            });
            // The session view will handle the line jumping
            return {
                success: true,
                lineNumber,
                message: `Navigating to line ${lineNumber}`
            };
        }
        catch (error) {
            console.error(`${this.DEBUG_PREFIX} Navigation error:`, error);
            return {
                success: false,
                message: 'Failed to navigate to session'
            };
        }
    }
    /**
     * Scroll to line within current session
     * Used from session-level gems
     *
     * @param lineNumber - Line to scroll to
     * @param context - Navigation context
     */
    static async scrollToLineInSession(lineNumber, context) {
        // Emit event for transcript component to handle scrolling
        // Using Vue's event bus or store
        const event = new CustomEvent('gem-scroll-to-line', {
            detail: { lineNumber }
        });
        window.dispatchEvent(event);
        return {
            success: true,
            lineNumber,
            message: `Jumping to line ${lineNumber}`
        };
    }
    /**
     * Highlight a transcript line temporarily
     * Creates a yellow glow effect that fades out
     *
     * @param lineElement - DOM element to highlight
     * @param duration - Duration in milliseconds
     */
    static highlightLine(lineElement, duration = 3000) {
        console.debug(`${this.DEBUG_PREFIX} Highlighting line element`);
        // Add highlight class
        lineElement.classList.add('transcript-line-highlight');
        // Remove highlight after duration
        setTimeout(() => {
            lineElement.classList.remove('transcript-line-highlight');
        }, duration);
    }
}
